/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { get } from 'lodash';
import withRouter from 'react-router/withRouter';
import loadData from './brands_that_matter_page_state_manager';
import {
  airtableMarkdownToHtml,
  parseApiTableResponse
} from '../../../../utils/airtable';
import loadable from '@loadable/component';
import { SplashPage } from '../../../fc-components/main/components';
import SimpleList from './components/SimpleList/SimpleList';
import { UserConsumer } from '../../../components/context/userContext';
import PremiumPaywall from '../../../components/PremiumPaywall/PremiumPaywall';

const AdContainer = loadable(() =>
  import('../../../components/ad/AdContainer')
);
// const NotFound = loadable(() =>
//   import('../../../components/not_found_component/not_found_component')
// );
const BrandsThatMatter = loadable(
  () => import('../../../fc-components/main/containers'),
  {
    resolveComponent: components => components.BrandsThatMatter
  }
);
const Section = loadable(
  () => import('../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Section
  }
);
const Image = loadable(() => import('../../../fc-components/main/components'), {
  resolveComponent: components => components.Image
});
const Text = loadable(() => import('../../../fc-components/main/components'), {
  resolveComponent: components => components.Text
});
const FranchiseTopper = loadable(
  () => import('../../../fc-components/main/components'),
  {
    resolveComponent: components => components.FranchiseTopper
  }
);
const StickyBar = loadable(
  () => import('../../../fc-components/main/components/StickyBar/StickyBar'),
  {
    resolveComponent: components => components.StickyBar
  }
);
const Button = loadable(
  () => import('../../../fc-components/main/components'),
  {
    resolveComponent: components => components.Button
  }
);

const BrandsThatMatterPage = ({ franchiseYear, brandsThatMatterPage }) => {
  const [activeTab, setActiveTab] = useState(0);
  const showSplash = false;

  /**
   * Returns an object containing the headline and subheadline for the Brands That Matter list page.
   * @param {Object} data - An object containing data from Airtable and WordPress.
   * @param {Object} data.wpData - Data from WordPress.
   * @param {Object} data.airtableData - Data from Airtable.
   * @returns {Object} An object containing the headline and subheadline.
   */
  const getTitles = data => {
    const { wpData, airtableData } = data;

    // Headlines
    const airtableTitle = airtableData?.Metadata?.[0]?.PageTitle;
    const wordpressTitle = wpData?.title?.rendered;
    const defaultHeadline = 'Brands That Matter';
    const headline = airtableTitle || wordpressTitle || defaultHeadline;

    // Subheadlines
    const airtableSubHeadline = airtableData?.Metadata?.[0]?.Subhead;
    const wordpressSubHeadline = wpData?.acf?.franchise_dek;
    const defaultSubHeadline = '';
    const subHeadline =
      airtableSubHeadline || wordpressSubHeadline || defaultSubHeadline;

    // Share text
    const airtableShareText = airtableData?.Metadata?.[0]?.shareText;
    const defaultShareText = '';
    const shareText = airtableShareText || defaultShareText;

    return {
      headline,
      subHeadline,
      shareText
    };
  };

  const buildTitleElement = ({ headline, subHeadline }) => (
    <Section bgColor="transparent" name="franchise-headline" maxWidth={970}>
      <FranchiseTopper headline={headline} subHeadline={subHeadline} />
    </Section>
  );

  /**
   * Returns hero images and caption for a given data object.
   * @param {Object} data - The data object containing wpData and airtableData.
   * @param {Object} data.wpData - The WordPress data object.
   * @param {Object} data.airtableData - The Airtable data object.
   * @returns {Object} An object containing featureImageDesktop, featureImageMobile, and franchiseHeroImageCaption.
   */
  const getHeros = data => {
    const { wpData, airtableData } = data;

    // Desktop Hero image
    const airtableFeatureImageDesktop =
      airtableData?.Metadata?.[0]?.['featureimage (desktop)'];
    const wordpressFeatureImageDesktop =
      wpData?.acf?.franchise_images?.franchise_hero_image_desktop;
    const defaultFeatureImageDesktop = undefined;
    const featureImageDesktop =
      airtableFeatureImageDesktop ||
      wordpressFeatureImageDesktop ||
      defaultFeatureImageDesktop;

    // Mobile Hero image
    const airtableFeatureImageMobile =
      airtableData?.Metadata?.[0]?.['featureimage (mobile)'];
    const wordpressFeatureImageMobile =
      wpData?.acf?.franchise_images?.franchise_hero_image_mobile;
    const defaultFeatureImageMobile = undefined;
    const featureImageMobile =
      airtableFeatureImageMobile ||
      wordpressFeatureImageMobile ||
      defaultFeatureImageMobile;

    // Hero image caption
    const airtableFranchiseHeroImageCaption =
      airtableData?.Metadata?.[0]?.['featureimage caption'];
    const wordpressFranchiseHeroImageCaption =
      wpData?.acf?.franchise_images?.franchise_hero_image_caption;
    const defaultFranchiseHeroImageCaption = 'no caption';
    const franchiseHeroImageCaption =
      airtableFranchiseHeroImageCaption ||
      wordpressFranchiseHeroImageCaption ||
      defaultFranchiseHeroImageCaption;

    const featuredVideoDesktop =
      airtableData?.Metadata?.[0]?.featuredVideoDesktop;
    const featuredVideoMobile =
      airtableData?.Metadata?.[0]?.featuredVideoMobile;

    return {
      featureImageDesktop,
      featureImageMobile,
      franchiseHeroImageCaption,
      featuredVideoDesktop,
      featuredVideoMobile
    };
  };

  const buildHeroElement = ({
    desktopImage,
    mobileImage,
    featuredVideoDesktop,
    featuredVideoMobile,
    caption
  }) => (
    <Section bgColor="transparent" maxWidth={970} name="franchise-hero">
      {featuredVideoDesktop ? (
        <video autoPlay loop muted playsInline>
          <source
            src={featuredVideoMobile}
            type="video/mp4"
            media="(max-width: 767px)"
          />
          <source src={featuredVideoDesktop} type="video/mp4" />
          <track kind="captions" src="" srclang="en" label="English" />
        </video>
      ) : (
        <>
          <div className="desktopHeroImage">
            <Image
              image={desktopImage}
              caption={airtableMarkdownToHtml(caption)}
            />
          </div>
          <div className="mobileHeroImage">
            <Image
              image={mobileImage}
              caption={airtableMarkdownToHtml(caption)}
            />
          </div>
        </>
      )}
    </Section>
  );

  /**
   * Returns an object containing contributor text data from the given data object.
   * @param {Object} data - The data object containing wpData and airtableData.
   * @param {Object} data.wpData - The WordPress data object.
   * @param {Object} data.airtableData - The Airtable data object.
   * @returns {Object} An object containing contributor text data.
   */
  const getContributorData = data => {
    const { wpData, airtableData } = data;

    // Contributor text
    const airtableContributorText = airtableData?.Metadata?.[0]?.Contributors;
    const wordpressContributorText = wpData?.acf?.franchiseContributors;
    const defaultContributorText = '';
    const contributorText =
      airtableContributorText ||
      wordpressContributorText ||
      defaultContributorText;

    return {
      contributorText
    };
  };

  const buildContributorsElement = ({ text }) => (
    <Section
      addClass="creditsSection"
      bgColor="transparent"
      name="franchise-contributors"
      noPadding
      maxWidth={1080}
    >
      <span
        className="section__franchise-credits--content"
        dangerouslySetInnerHTML={{ __html: text }}
      />
      {franchiseYear === 2024 && (
        <div className="creditThumb">
          <img
            src="https://assets.fastcompany.com/image/upload/v1732130360/Brands%20That%20Matter/2024%20BTM/btm24_final_footer_toons_2.png"
            alt=""
            maxWidth={315}
          />
        </div>
      )}
    </Section>
  );

  /**
   * Returns an object containing data for a blurb, including an image and text.
   * @param {Object} data - An object containing data from Airtable and WordPress.
   * @param {Object} data.wpData - Data from WordPress.
   * @param {Object} data.airtableData - Data from Airtable.
   * @returns {Object} An object containing data for a blurb, including an image and text.
   */
  const getBlurbData = data => {
    const { wpData, airtableData } = data;

    // Blurb image
    const airtableBlurbImage = airtableData?.Metadata?.[0]?.['Blurb image'];
    const wordpressBlurbImage =
      wpData?.acf?.franchise_images?.franchise_blurb_image;
    const defaultBlurbImage = undefined;
    const blurbImage =
      airtableBlurbImage || wordpressBlurbImage || defaultBlurbImage;

    // Blurb text
    const airtableBlurb = airtableData?.Metadata?.[0]?.Blurb;
    const wordpressFranchiseBlurb = wpData?.acf?.franchise_blurb;
    const defaultFranchiseBlurb = '';
    const franchiseBlurb =
      airtableBlurb || wordpressFranchiseBlurb || defaultFranchiseBlurb;

    return {
      blurbImage,
      franchiseBlurb
    };
  };

  /**
   * Returns an object containing the splash image data.
   *
   * @param {Object} data - The data object containing the airtable data.
   * @param {Object} data.airtableData - The airtable data object.
   * @param {Object} data.airtableData.metadata - The metadata object containing the splash page image.
   * @param {string} data.airtableData.metadata.splashPageImage - The URL of the splash page image.
   * @returns {Object} An object containing the splash image data.
   */
  const getSplashData = data => {
    const { airtableData } = data;

    // Splash image
    const airtableSplashImage = airtableData?.Metadata?.[0]?.splashPageImage;
    const defaultSplashImage = '';
    const splashImage = airtableSplashImage || defaultSplashImage;
    const airtableSplashMessage = airtableData?.Metadata?.[0]?.splashPageText;
    const defaultSplashMessage = '';
    const splashMessage = airtableSplashMessage || defaultSplashMessage;

    return {
      splashImage,
      splashMessage
    };
  };

  /**
   * Returns an object containing data for the "Apply" section of the Brands That Matter list page.
   * @param {Object} data - An object containing data from Airtable.
   * @param {Object} data.airtableData - An object containing data from Airtable.
   * @param {string} [data.airtableData.metadata.registerTitle] - The title for the "Apply" section, if available.
   * @param {string} [data.airtableData.metadata.registerCopy] - The subheadline for the "Apply" section, if available.
   * @param {string} [data.airtableData.metadata.registerCTA-Copy] - The text for the "Apply" button, if available.
   * @returns {Object} An object containing data for the "Apply" section of the Brands That Matter list page.
   */
  const getApplyData = data => {
    const { airtableData } = data;

    // Splash image
    const airtableApplyTitle = airtableData?.Metadata?.[0]?.registerTitle;
    const defaultApplyTitle = "Register for Next Year's list";
    const applyTitle = airtableApplyTitle || defaultApplyTitle;
    const airtableApplySubHeadline = airtableData?.Metadata?.[0]?.registerCopy;
    const defaultApplySubHeadline =
      'Register here to be among the first notified when applications open!';
    const applySubHeadline =
      airtableApplySubHeadline || defaultApplySubHeadline;
    const airtableApplyButtonText =
      airtableData?.Metadata?.[0]?.['registerCTA-Copy'];
    const defaultApplyButtonText = 'Request Notifications';
    const applyButtonText = airtableApplyButtonText || defaultApplyButtonText;
    const airtableApplyButtonLink = airtableData?.Metadata?.[0]?.registerLink;
    const defaultApplyButtonLink = '#';
    const applyButtonLink = airtableApplyButtonLink || defaultApplyButtonLink;

    return {
      applyTitle,
      applySubHeadline,
      applyButtonText,
      applyButtonLink
    };
  };

  /**
   * Returns an object containing the previous years' title and subheadline.
   * @param {Object} data - The data object containing airtableData.
   * @returns {Object} An object containing the previous years' title and subheadline.
   */
  const getPreviousYearsData = data => {
    const { airtableData } = data;

    // Splash image
    const airtablePreviousYearsTitle =
      airtableData?.Metadata?.[0]?.pastYearsTitle;
    const defaultPreviousYearsTitle = "See Previous Year's Lists";
    const previousYearsTitle =
      airtablePreviousYearsTitle || defaultPreviousYearsTitle;
    const airtablePreviousYearsSubHeadline =
      airtableData?.Metadata?.[0]?.pastYearsCopy;
    const defaultPreviousYearsSubHeadline =
      'Explore winners and honorees from years past!';
    const previousYearsSubHeadline =
      airtablePreviousYearsSubHeadline || defaultPreviousYearsSubHeadline;

    return {
      previousYearsTitle,
      previousYearsSubHeadline
    };
  };

  const buildBlurbElement = ({ imageUrl, text }) => (
    <Section bgColor="transparent" name="franchise-blurb">
      <div className="introText">
        <Text>
          {imageUrl && <Image image={imageUrl} width="110px" float="left" />}
          <div
            dangerouslySetInnerHTML={{ __html: airtableMarkdownToHtml(text) }}
          />
        </Text>
      </div>
    </Section>
  );

  /**
   * Returns normalized data from the given list data.
   * @param {Object} listData - The list data to normalize.
   * @param {Object} listData.airtableData - The Airtable data to normalize.
   * @returns {Object} The normalized data.
   */
  const getListData = listData => {
    const { airtableData } = listData;
    const hasRecords = Object.keys(airtableData).length > 0;
    const needsNormalization =
      hasRecords && 'records' in airtableData[Object.keys(airtableData)[0]];

    const normalizedData = airtableData;
    if (needsNormalization) {
      Object.keys(airtableData).forEach(tableName => {
        normalizedData[tableName] = parseApiTableResponse(
          airtableData[tableName]
        );
      });
    }

    return normalizedData;
  };

  const handleTabClick = index => {
    setActiveTab(index);
  };

  const buildListElement = listData => {
    const {
      type,
      data,
      interstitialUnit,
      tab1TableName,
      tab2TableName,
      tab1Label,
      tab2Label
    } = listData;

    const tab1Data = data[tab1TableName];
    const tab2Data = data[tab2TableName];

    switch (type) {
      case 'legacy': {
        return (
          <section className="section__franchise-list">
            <div className="section__inner">
              <div className="tab__container">
                <div
                  className={`tab__container--tab ${
                    activeTab === 0 ? 'active' : ''
                  }`}
                  onClick={() => handleTabClick(0)}
                >
                  {activeTab === 0 ? (
                    <mark>{tab1Label}</mark>
                  ) : (
                    <>{tab1Label}</>
                  )}
                </div>
                <div
                  className={`tab__container--tab ${
                    activeTab === 1 ? 'active' : ''
                  }`}
                  onClick={() => handleTabClick(1)}
                >
                  {activeTab === 1 ? (
                    <mark>{tab2Label}</mark>
                  ) : (
                    <>{tab2Label}</>
                  )}
                </div>
              </div>
              <UserConsumer>
                {({ hasPaywallAccess }) => {
                  const showDrawer =
                    !hasPaywallAccess() && franchiseYear !== 2025;
                  return (
                    <div className="franchiseList__container">
                      {activeTab === 0 && (
                        <SimpleList
                          items={
                            showDrawer ? [...tab1Data].slice(0, 2) : tab1Data
                          }
                          interstitial={interstitialUnit}
                        />
                      )}
                      {activeTab === 1 && (
                        <SimpleList
                          items={
                            showDrawer ? [...tab2Data].slice(0, 2) : tab2Data
                          }
                          interstitial={interstitialUnit}
                        />
                      )}
                      {showDrawer && <PremiumPaywall />}
                    </div>
                  );
                }}
              </UserConsumer>
            </div>
          </section>
        );
      }
      default:
        return null;
    }
  };

  const buildApplyElement = ({
    title,
    subHeadline,
    buttonText,
    buttonLink
  }) => (
    <Section
      name="franchise-apply"
      bgColor="transparent"
      noPadding
      maxWidth={1080}
    >
      <h2>{title}</h2>
      <p>{subHeadline}</p>
      <a href={buttonLink}>
        <Button
          bgColor="#000"
          labelColor="#FFFFFF"
          labelHoverColor="#FFFFFF"
          hoverColor="#D7D7D7"
          rounded
          label={buttonText}
        />
      </a>
    </Section>
  );

  const buildPreviousYearsElement = ({ title, subHeadline, years = [] }) => (
    <Section
      addClass="perviousYearsSection"
      name="franchise-previousYears"
      bgColor="transparent"
      noPadding
      maxWidth={1080}
    >
      <h2>{title}</h2>
      <p>{subHeadline}</p>
      <form>
        <div className="select-container">
          <select
            onChange={e => {
              e.preventDefault();

              // Bail if user chooses the default option
              if (e.target.value === 'Choose Year') return;

              // when the select box is changed, open the selected year's page in a new tab
              const selectedYear = e.target.value;
              const selectedYearPath = `/brands-that-matter/${selectedYear}`;
              const newWindow = window.open(
                selectedYearPath,
                '_blank',
                'noopener,noreferrer'
              );
              if (newWindow) newWindow.opener = null;
            }}
            name="yearDropdown"
          >
            <option className="defaultOption">Choose Year</option>
            {years.map(year => (
              <option key={year.label} value={year.label}>
                {year.label}
              </option>
            ))}
          </select>
        </div>
      </form>
    </Section>
  );

  const jumpToListTop = () => {
    const JUMP_DISTANCE_OFFSET = 60;
    const distance =
      window.scrollY +
      document
        .querySelector('.section__franchise-list')
        ?.getBoundingClientRect()?.top;
    window.scrollTo({
      top: distance - JUMP_DISTANCE_OFFSET,
      left: 0,
      behavior: 'smooth'
    });
  };

  const shareIcon = () => (
    <svg
      width="20"
      height="21"
      viewBox="0 0 20 21"
      fill="none"
      xmlns="https://www.w3.org/2000/svg"
    >
      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M3.36328 12.4871C2.80265 12.4873 2.26505 12.2647 1.8684 11.8684C1.47189 11.4723 1.24901 10.9348 1.24887 10.3743C1.24872 9.81371 1.47116 9.27606 1.86736 8.87961C2.26358 8.48317 2.80117 8.26019 3.36181 8.25993C3.92229 8.25964 4.46018 8.48204 4.85669 8.87818C5.25335 9.27433 5.47633 9.81168 5.47678 10.3722C5.47663 10.9328 5.25391 11.4703 4.85769 11.8669C4.46133 12.2634 3.92388 12.4864 3.36324 12.4871H3.36328ZM16.6141 14.8836C17.1748 14.8836 17.7124 15.1063 18.1087 15.5026C18.5052 15.8987 18.728 16.4362 18.7281 16.9968C18.7283 17.5572 18.5057 18.0948 18.1095 18.4912C17.7131 18.8877 17.1757 19.1105 16.615 19.1108C16.0545 19.1109 15.5168 18.8885 15.1201 18.4925C14.7236 18.0964 14.5006 17.559 14.5002 16.9985C14.5003 16.4378 14.7231 15.9002 15.1194 15.5036C15.5158 15.1072 16.0534 14.8841 16.6142 14.8836L16.6141 14.8836ZM16.6141 1.63643C17.1749 1.63643 17.7127 1.85926 18.1091 2.2557C18.5055 2.65215 18.7282 3.1898 18.7281 3.75045C18.7279 4.31114 18.5051 4.84879 18.1084 5.24509C17.7118 5.64138 17.174 5.86392 16.6133 5.86363C16.0525 5.86334 15.5149 5.64036 15.1185 5.2438C14.7223 4.84721 14.4999 4.30929 14.5002 3.74876C14.5009 3.18851 14.7238 2.65145 15.1201 2.25545C15.5165 1.85945 16.0538 1.63672 16.6141 1.63643ZM16.6141 7.10915C17.56 7.1093 18.4623 6.71112 19.0996 6.01225C19.7369 5.31337 20.0504 4.37843 19.963 3.4367C19.8757 2.49483 19.3957 1.63344 18.6409 1.06369C17.8859 0.49379 16.9259 0.268226 15.9961 0.442125C15.0663 0.616161 14.2527 1.17354 13.755 1.97778C13.2573 2.78201 13.1214 3.75871 13.3805 4.66833L6.03822 8.33831C5.46649 7.58689 4.60511 7.11044 3.66455 7.02565C2.72411 6.94072 1.79133 7.25525 1.09421 7.89209C0.397098 8.52905 0 9.42961 0 10.3738C0 11.3179 0.397061 12.2185 1.09421 12.8555C1.79133 13.4923 2.72414 13.8068 3.66455 13.7219C4.60514 13.6371 5.46653 13.1607 6.03822 12.4092L13.3805 16.0792C13.0685 17.1666 13.3221 18.3379 14.0559 19.199C14.7899 20.06 15.9064 20.496 17.0297 20.3604C18.153 20.2247 19.1335 19.5353 19.6412 18.5243C20.1488 17.5134 20.1161 16.3153 19.5539 15.3336C18.9918 14.3518 17.9751 13.7169 16.8461 13.6427C15.7172 13.5686 14.626 14.0649 13.9403 14.9646L6.59649 11.2921C6.7681 10.6919 6.7681 10.0557 6.59649 9.45556L13.9403 5.783C14.5752 6.61863 15.5644 7.10948 16.6141 7.10948L16.6141 7.10915Z"
        fill="black"
      />
    </svg>
  );

  const { headline, subHeadline, shareText } = getTitles(
    brandsThatMatterPage.data
  );
  const {
    featureImageDesktop,
    featureImageMobile,
    franchiseHeroImageCaption,
    featuredVideoDesktop,
    featuredVideoMobile
  } = getHeros(brandsThatMatterPage.data);
  const { blurbImage, franchiseBlurb } = getBlurbData(
    brandsThatMatterPage.data
  );
  const listData = getListData(brandsThatMatterPage.data);
  const { contributorText } = getContributorData(brandsThatMatterPage.data);
  const { splashImage, splashMessage } = getSplashData(
    brandsThatMatterPage.data
  );
  const {
    applyTitle,
    applySubHeadline,
    applyButtonText,
    applyButtonLink
  } = getApplyData(brandsThatMatterPage.data);
  const { previousYearsTitle, previousYearsSubHeadline } = getPreviousYearsData(
    brandsThatMatterPage.data
  );

  let customConfiguration;

  if (franchiseYear === 2022) {
    customConfiguration = {
      sections: {
        title: {
          type: 'default',
          headline,
          subHeadline
        },
        hero: {
          type: 'default',
          desktopImage: featureImageDesktop,
          mobileImage: featureImageMobile,
          caption: franchiseHeroImageCaption
        },
        blurb: {
          type: 'default',
          imageUrl: blurbImage,
          text: franchiseBlurb
        },
        list: {
          type: 'legacy',
          tab1TableName: 'BTM winners',
          tab2TableName: 'Categories',
          tab1Label: 'Brands That Matter',
          tab2Label: 'Categories',
          interstitialUnit: (
            <React.Fragment>
              <AdContainer type="break" />
            </React.Fragment>
          ),
          interstitialFreq: 10,
          activeTab,
          setActiveTab
        },
        contributors: {
          type: 'default',
          text: contributorText
        }
      }
    };
  }

  if (franchiseYear === 2023) {
    customConfiguration = {
      sections: {
        title: {
          type: 'default',
          headline,
          subHeadline
        },
        hero: {
          type: 'default',
          desktopImage: featureImageDesktop,
          mobileImage: featureImageMobile,
          caption: franchiseHeroImageCaption
        },
        blurb: {
          type: 'default',
          imageUrl: blurbImage,
          text: franchiseBlurb
        },
        list: {
          type: 'legacy',
          tab1TableName: 'Winners',
          tab2TableName: 'Honorees',
          tab1Label: 'Brands That Matter',
          tab2Label: 'Categories',
          interstitialUnit: (
            <React.Fragment>
              <AdContainer type="break" />
            </React.Fragment>
          ),
          interstitialFreq: 10,
          activeTab,
          setActiveTab
        },
        apply: {
          title: applyTitle,
          subHeadline: applySubHeadline,
          buttonText: applyButtonText,
          buttonLink: applyButtonLink
        },
        previousYears: {
          title: previousYearsTitle,
          subHeadline: previousYearsSubHeadline,
          years: [
            {
              label: '2022',
              link: '/brands-that-matter/2022'
            },
            {
              label: '2021',
              link: '/brands-that-matter/2021'
            }
          ]
        },
        contributors: {
          type: 'default',
          text: contributorText
        }
      }
    };
  }

  if (franchiseYear === 2024) {
    customConfiguration = {
      sections: {
        title: {
          type: 'default',
          headline,
          subHeadline
        },
        hero: {
          type: 'default',
          desktopImage: featureImageDesktop,
          mobileImage: featureImageMobile,
          featuredVideoDesktop,
          featuredVideoMobile,
          caption: franchiseHeroImageCaption
        },
        blurb: {
          type: 'default',
          imageUrl: blurbImage,
          text: franchiseBlurb
        },
        list: {
          type: 'legacy',
          tab1TableName: 'Winners',
          tab2TableName: 'Honorees',
          tab1Label: 'Brands That Matter',
          tab2Label: 'Categories',
          interstitialUnit: (
            <React.Fragment>
              <AdContainer type="break" />
            </React.Fragment>
          ),
          interstitialFreq: 10,
          activeTab,
          setActiveTab
        },
        apply: {
          title: applyTitle,
          subHeadline: applySubHeadline,
          buttonText: applyButtonText,
          buttonLink: applyButtonLink
        },
        previousYears: {
          title: previousYearsTitle,
          subHeadline: previousYearsSubHeadline,
          years: [
            {
              label: '2023',
              link: '/brands-that-matter/2023'
            },
            {
              label: '2022',
              link: '/brands-that-matter/2022'
            },
            {
              label: '2021',
              link: '/brands-that-matter/2021'
            }
          ]
        },
        contributors: {
          type: 'default',
          text: contributorText
        }
      }
    };
  }

  if (franchiseYear === 2025) {
    customConfiguration = {
      sections: {
        title: {
          type: 'default',
          headline,
          subHeadline
        },
        hero: {
          type: 'default',
          desktopImage: featureImageDesktop,
          mobileImage: featureImageMobile,
          featuredVideoDesktop,
          featuredVideoMobile,
          caption: franchiseHeroImageCaption
        },
        blurb: {
          type: 'default',
          imageUrl: blurbImage,
          text: franchiseBlurb
        },
        list: {
          type: 'legacy',
          tab1TableName: 'Winners',
          tab2TableName: 'Honorees',
          tab1Label: 'Brands That Matter',
          tab2Label: 'Categories',
          interstitialUnit: (
            <React.Fragment>
              <AdContainer type="break" />
            </React.Fragment>
          ),
          interstitialFreq: 10,
          activeTab,
          setActiveTab
        },
        apply: {
          title: applyTitle,
          subHeadline: applySubHeadline,
          buttonText: applyButtonText,
          buttonLink: applyButtonLink
        },
        previousYears: {
          title: previousYearsTitle,
          subHeadline: previousYearsSubHeadline,
          years: [
            {
              label: '2024',
              link: '/brands-that-matter/2024'
            },
            {
              label: '2023',
              link: '/brands-that-matter/2023'
            },
            {
              label: '2022',
              link: '/brands-that-matter/2022'
            },
            {
              label: '2021',
              link: '/brands-that-matter/2021'
            }
          ]
        },
        contributors: {
          type: 'default',
          text: contributorText
        }
      }
    };

    if (showSplash) {
      customConfiguration.splash = {
        franchiseName: 'Brands That Matter 2025',
        splashMessage,
        previousYearUrl: '/brands-that-matter/2024',
        previousYearText: "See last year's list",
        applicationUrl: '/brands-that-matter/apply',
        applicationText: 'APPLY TO the 2026 LIST →',
        logoSrc: splashImage,
        colorObj: {
          backgroundColor: '#b883ff'
        }
      };
    }
  }

  // Dynamic list data
  if ('list' in customConfiguration?.sections) {
    customConfiguration.sections.list.data = listData;
  }

  const buildFromConfig = configuration =>
    Object.keys(configuration?.sections || {}).map(section => {
      switch (section) {
        case 'title': {
          return buildTitleElement(configuration.sections[section]);
        }
        case 'hero': {
          return buildHeroElement(configuration.sections[section]);
        }
        case 'blurb': {
          return buildBlurbElement(configuration.sections[section]);
        }
        case 'list': {
          return buildListElement(configuration.sections[section]);
        }
        case 'contributors': {
          return buildContributorsElement(configuration.sections[section]);
        }
        case 'apply': {
          return buildApplyElement(configuration.sections[section]);
        }
        case 'previousYears': {
          return buildPreviousYearsElement(configuration.sections[section]);
        }
        default:
          return null;
      }
    });

  // Splash page logic
  if ('splash' in customConfiguration) {
    return (
      <BrandsThatMatter splash year={franchiseYear}>
        <SplashPage {...customConfiguration.splash} />
        <AdContainer type="break" />
      </BrandsThatMatter>
    );
  }

  return (
    <BrandsThatMatter year={franchiseYear}>
      <div id="btmEditorialLander" className="btm-2025-editorial-check">
        {buildFromConfig(customConfiguration)}
      </div>
      <StickyBar
        slug="btm-2025"
        externalData={listData} // Category data coming from Airtable not WP
        location="article"
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        overrideShareIcon={shareIcon()}
        overrideSocialText={encodeURIComponent(shareText)}
        post={{
          id: 'brands-that-matter',
          slug: 'list',
          title: 'Fast Company'
        }}
        initialData={{
          background_color: '#000000',
          display_type: 'custom',
          border_color: 'transparent',
          text_color: '#000',
          link_url: '/brands-that-matter/list',
          display_text: 'Brands That Matter Display Text'
        }}
        initialLists={[
          { id: 'categories', category: 'Brands That Matter' },
          { id: 'honorees', category: 'Categories' }
        ]}
        scroll={jumpToListTop}
      />
    </BrandsThatMatter>
  );
};

BrandsThatMatterPage.loadData = store => loadData(store);

function mapStateToProps(state = {}) {
  return {
    config: state.config,
    pageStatus: state.status.code,
    brandsThatMatterPage: get(state, 'brandsThatMatterPage') || {
      error: true
    }
  };
}

BrandsThatMatterPage.propTypes = {};

export default withRouter(connect(mapStateToProps)(BrandsThatMatterPage));
